import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/internal/Observable';
import { map, startWith } from 'rxjs/operators';
import { countryCities } from 'src/app/shared/data/countryCities.json';
import { EnumEconomicActivity } from 'src/app/shared/enums/economic-activity.enum';
import { ICountryCity } from 'src/app/shared/interfaces/country-city.interface';
import { CustomerDataService } from 'src/app/shared/services/customer-data.service';
import { ValidMatcher } from 'src/app/shared/utils/valid-mather.matcher';
import { CustomValidators } from 'src/app/shared/validators/custom-validators.validators';
import { cities } from 'src/app/shared/data/cities.json';
import { routesData } from 'src/app/shared/resources/routes.constant';
import { RegExps } from 'src/app/shared/resources/regexps.constant';
import { ICrmData } from 'src/app/shared/interfaces/crm-data.interface';
import { EnumStepFunctions } from 'src/app/shared/enums/step-functions.enum';
import { CrmCdtAccountsCustomerService } from 'src/app/services/crm-cdt-accounts-customer.service';
import { Subscription } from 'rxjs';
import { ICity } from 'src/app/shared/interfaces/city.interface';
import { capitalize } from 'src/app/shared/utils/capitalize';
import { MoreInfoService } from 'src/app/shared/services/more-info.service';
import { AnalyticService } from 'src/app/shared/services/analytic.service';

@Component({
  selector: 'app-contact-information',
  templateUrl: './contact-information.component.html',
  styleUrls: ['./contact-information.component.scss']
})
export class ContactInformationComponent implements OnInit, OnDestroy {
  get groupWorkplace(): UntypedFormGroup {
    return this.contactInformationForm.get(
      'workplaceGroup'
    ) as UntypedFormGroup;
  }

  get groupAddress(): UntypedFormGroup {
    return this.contactInformationForm.get('addressGroup') as UntypedFormGroup;
  }

  get controlHomeCity(): AbstractControl {
    return this.contactInformationForm.get('addressGroup.homeCity');
  }

  get controlWorkCity(): AbstractControl {
    return this.contactInformationForm.get('workplaceGroup.workCity');
  }

  get controlWorkPhone(): AbstractControl {
    return this.contactInformationForm.get('workplaceGroup.workPhone');
  }

  get controlEmail(): AbstractControl {
    return this.contactInformationForm.get('emailGroup.email');
  }

  get controlEmailConfirm(): AbstractControl {
    return this.contactInformationForm.get('emailGroup.emailConfirm');
  }

  contactInformationForm: UntypedFormGroup = {} as UntypedFormGroup;
  confirmValidMatcher = new ValidMatcher();
  filteredHomeCities: Observable<ICity[]> = new Observable();
  filteredWorkCities: Observable<ICountryCity[]> = new Observable();
  bannedWords: string[] = [
    'defini',
    'tiene',
    'aplica',
    'sin direccion',
    'misma',
    'especifi',
    '@',
    'sindirección',
    'sindireccion',
    'sin dirección'
  ];
  preloadCrmData: {
    emailGroup: {
      email: string;
      emailConfirm: string;
    };
    landlinePhone: string;
    addressGroup?: {
      homeAddress: string;
      homeCity: ICity;
      homeDistrict: string;
      landlinePhone: string;
    };
    workplaceGroup?: {
      companyName: string;
      workPhone: string;
      workAddress: string;
      workCity: ICountryCity;
    };
  } = {
    emailGroup: {
      email: '',
      emailConfirm: ''
    },
    landlinePhone: '',
    addressGroup: {
      homeAddress: '',
      homeCity: {} as ICity,
      homeDistrict: '',
      landlinePhone: ''
    },
    workplaceGroup: {
      companyName: '',
      workPhone: '',
      workAddress: '',
      workCity: {} as ICountryCity
    }
  };
  webSocketSubscribe = new Subscription();
  private normalizeForm = 'NFD';
  crmHomeCity: ICity = {} as ICity;
  crmHomeAddress: string = '';
  crmHomeDistrict: string = '';
  userGroupDisabled: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private customerDataService: CustomerDataService,
    private crmCdtAccountsCustomerService: CrmCdtAccountsCustomerService,
    private router: Router,
    private moreInfoService: MoreInfoService,
    public analytic: AnalyticService
  ) {
    this.loadCrmData();
  }

  ngOnInit() {
    this.contactInformationForm = this.fb.group({
      emailGroup: this.fb.group(
        {
          email: [
            this.preloadCrmData.emailGroup.email,
            [Validators.nullValidator]
          ],
          emailConfirm: [
            { value: this.preloadCrmData.emailGroup.email, disabled: true },
            [Validators.nullValidator]
          ]
        },
        { validators: CustomValidators.groupEqual({ insensitive: true }) }
      ),
      landlinePhone: [
        this.preloadCrmData.landlinePhone,
        [
          Validators.minLength(7),
          Validators.maxLength(7),
          CustomValidators.landlinePhone
        ]
      ],
      addressGroup: this.fb.group({
        homeAddress: [
          this.preloadCrmData.addressGroup.homeAddress,
          [
            Validators.required,
            Validators.minLength(5),
            Validators.maxLength(60),
            Validators.pattern(/^[^\d]/),
            CustomValidators.bannedWords(this.bannedWords),
            CustomValidators.addressRulesValidator
          ]
        ],
        homeCity: [
          this.preloadCrmData.addressGroup.homeCity,
          [Validators.required, CustomValidators.notAString]
        ],
        homeDistrict: [
          this.preloadCrmData.addressGroup.homeDistrict,
          [Validators.required, Validators.minLength(2)]
        ],
        landlinePhone: [
          this.preloadCrmData.addressGroup.landlinePhone,
          [
            Validators.minLength(7),
            Validators.maxLength(7),
            CustomValidators.landlinePhone
          ]
        ]
      }),

      workplaceGroup: this.fb.group({
        companyName: [
          this.preloadCrmData.workplaceGroup.companyName,
          {
            validators: [
              Validators.required,
              Validators.minLength(3),
              Validators.maxLength(50)
            ],
            updateOn: 'change'
          }
        ],
        workPhone: [this.preloadCrmData.workplaceGroup.workPhone],
        workAddress: [
          this.preloadCrmData.workplaceGroup.workAddress,
          {
            validators: [
              Validators.required,
              Validators.minLength(5),
              Validators.maxLength(60),
              Validators.pattern(/^[^\d]/),
              CustomValidators.bannedWords(this.bannedWords),
              CustomValidators.addressRulesValidator
            ],
            updateOn: 'change'
          }
        ],
        workCity: [
          this.preloadCrmData.workplaceGroup.workCity,
          [Validators.required, CustomValidators.notAString]
        ]
      })
    });
    this.filterOptions();
    this.toggleHideAddress();
    this.toggleHideWorkplace();
    this.toggleDisableConfirmEmail();
  }

  ngOnDestroy() {
    this.webSocketSubscribe.unsubscribe();
  }

  loadCrmData(): void {
    this.webSocketSubscribe.add(
      this.crmCdtAccountsCustomerService
        .observerStepFunctionData$(EnumStepFunctions.StepReadCrm)
        .subscribe({
          next: (crm: ICrmData) => {
            this.crmHomeCity =
              this.customerDataService.customerData.client && crm.contactCity
                ? this._filterHomeCityByCodeDane(+crm.contactCity)
                : null;
            this.crmHomeAddress =
              this.customerDataService.customerData.client &&
              crm.addresessList.length
                ? capitalize(crm.addresessList[0].trim())
                : '';
            this.crmHomeDistrict =
              this.customerDataService.customerData.client && crm.neighborhood
                ? capitalize(crm.neighborhood.trim())
                : '';
            const landlinePhone =
              this.customerDataService.customerData.client &&
              crm.contactPhonesList.length
                ? crm.contactPhonesList
                    .filter((el) => el.phoneType === 'Fijo')[0]
                    .phoneNumber.trim()
                : '';
            this.preloadDataCRM(crm, landlinePhone);

            if (this.preloadCrmData.landlinePhone === '0') {
              this.preloadCrmData.landlinePhone = '';
            }

            if (this.preloadCrmData.addressGroup.landlinePhone === '0') {
              this.preloadCrmData.addressGroup.landlinePhone = '';
            }

            if (this.preloadCrmData.workplaceGroup.workPhone === '0') {
              this.preloadCrmData.workplaceGroup.workPhone = '';
            }

            if (this.contactInformationForm?.patchValue) {
              this.contactInformationForm.patchValue(this.preloadCrmData);
            }
          }
        })
    );
  }

  preloadDataCRM(crm: ICrmData, landlinePhone: string) {
    this.preloadCrmData = {
      emailGroup: {
        email:
          this.customerDataService.customerData.client &&
          crm.emailAdressContact.trim()
            ? crm.emailAdressContact.trim().toLowerCase()
            : '',
        emailConfirm:
          this.customerDataService.customerData.client &&
          crm.emailAdressContact.trim()
            ? crm.emailAdressContact.trim().toLowerCase()
            : ''
      },
      landlinePhone,
      addressGroup: {
        homeAddress: this.crmHomeAddress,
        homeCity: this.crmHomeCity,
        homeDistrict: this.crmHomeDistrict,
        landlinePhone
      },
      workplaceGroup: {
        companyName:
          this.customerDataService.customerData.client &&
          crm.employmentHistoryList.length
            ? crm.employmentHistoryList[0].companyName.trim()
            : '',
        workAddress:
          this.customerDataService.customerData.client &&
          crm.contactInfoList.length
            ? crm.contactInfoList[0].contactAddressList[0].trim()
            : '',
        workCity:
          this.customerDataService.customerData.client &&
          crm.employmentHistoryList.length
            ? this._filterWorkCityByCodeDane(
                +crm.employmentHistoryList[0].companyCityCode
              )
            : null,
        workPhone:
          this.customerDataService.customerData.client &&
          crm.contactInfoList.length
            ? crm.contactInfoList[0].contactPhoneList[0].trim()
            : ''
      }
    };
  }

  filterOptions(): void {
    this.filteredHomeCities = this.filterHomeCity(this.controlHomeCity);
    this.filteredWorkCities = this.filterWorkCity(this.controlWorkCity);
  }

  toggleHideAddress(): void {
    if (!this.moreInfoService.getMoreInfo().customerRelation) {
      this.groupAddress.enable();
    } else {
      this.groupAddress.disable();
      this.groupAddress.get('landlinePhone').enable();
      this.userGroupDisabled = true;
    }
  }

  toggleHideWorkplace(): void {
    try {
      if (
        this.customerDataService.customerData.economicActivityInformation
          .economicActivity === EnumEconomicActivity.Employee ||
        this.customerDataService.customerData.economicActivityInformation
          .economicActivity === EnumEconomicActivity.IndependentWithBusiness
      ) {
        this.groupWorkplace.enable();
      } else {
        this.groupWorkplace.disable();
      }

      if (
        this.customerDataService.customerData.economicActivityInformation
          .economicActivity === EnumEconomicActivity.Employee
      ) {
        this.controlWorkPhone.setValidators([
          Validators.required,
          Validators.minLength(7),
          CustomValidators.landlinePhone
        ]);
        this.controlWorkPhone.updateValueAndValidity();
      } else {
        this.controlWorkPhone.setValidators(CustomValidators.landlinePhone);
        this.controlWorkPhone.updateValueAndValidity();
      }
    } catch (error) {
      let errorMessage = '';
      if (error?.message !== undefined) {
        errorMessage = error.message;
      }
      this.analytic.uTagLinkError(
        'Error catch en toggleHideWorkplace() ' + errorMessage,
        'error_tech_page'
      );
      this.router.navigate([routesData.ERRORS.GENERAL_ERROR.redirectPath]);
    }
  }

  toggleDisableConfirmEmail(): void {
    this.controlEmail.valueChanges.subscribe({
      next: () => {
        this.controlEmailConfirm.reset();
        if (this.controlEmail.valid) {
          this.controlEmailConfirm.enable();
        } else {
          this.controlEmailConfirm.disable();
        }
      }
    });
  }

  updateCustomerData(): void {
    const val = this.contactInformationForm.value;
    let email = null;
    email = this.customerDataService.customerData.contactInformation.email;
    this.customerDataService.customerData = {
      ...this.customerDataService.customerData,
      ...{
        [EnumStepFunctions.StepReadCrm]: {
          ...this.customerDataService.customerData[
            EnumStepFunctions.StepReadCrm
          ],
          neighborhood: val.homeDistrict ? val.homeDistrict.trim() : null,
          addresessList: val.homeAddress ? [val.homeAddress.trim()] : [],
          contactCity: val.homeCity ? val.homeCity.codeDANE : null
        }
      },
      ...{
        contactInformation: this.updateContactInformation(email)
      }
    };
  }

  updateContactInformation(email: string) {
    const val = this.contactInformationForm.value;
    return {
      email,
      landlinePhone: this.updateLandlinePhone(val),
      homeAddress: val.addressGroup?.homeAddress
        ? val.addressGroup.homeAddress.trim()
        : this.crmHomeAddress,
      homeCity: val.addressGroup?.homeCity
        ? val.addressGroup.homeCity
        : this.crmHomeCity,
      homeDistrict: val.addressGroup?.homeDistrict
        ? val.addressGroup.homeDistrict.trim()
        : this.crmHomeDistrict,
      companyName: val.workplaceGroup?.companyName
        ? val.workplaceGroup.companyName.trim()
        : null,
      workPhone: val.workplaceGroup?.workPhone
        ? val.workplaceGroup.workPhone.trim()
        : null,
      workAddress: val.workplaceGroup?.workAddress
        ? val.workplaceGroup.workAddress.trim()
        : null,
      workCity: val.workplaceGroup?.workCity
        ? val.workplaceGroup.workCity
        : null
    };
  }

  updateLandlinePhone(val: any): string {
    let landlinePhone = null;
    if (val.addressGroup) {
      landlinePhone = val.addressGroup.landlinePhone
        ? val.addressGroup.landlinePhone.trim()
        : null;
    } else {
      landlinePhone = val.landlinePhone ? val.landlinePhone.trim() : null;
    }
    return landlinePhone;
  }

  workCityDisplayFn(city?: ICountryCity): string | undefined {
    return city ? city.municipality : undefined;
  }

  homeCityDisplayFn(city?: ICity): string | undefined {
    return city ? city.name : undefined;
  }

  filterWorkCity(control: AbstractControl): Observable<ICountryCity[]> {
    return control.valueChanges.pipe(
      startWith(''),
      map((value) =>
        typeof value === 'string' || value === null ? value : value.municipality
      ),
      map((name) => (name ? this._filterWorkCity(name) : countryCities.slice()))
    );
  }

  filterHomeCity(control: AbstractControl): Observable<ICity[]> {
    return control.valueChanges.pipe(
      startWith(''),
      map((value) =>
        typeof value === 'string' || value === null ? value : value.name
      ),
      map((name) => (name ? this._filterHomeCity(name) : cities.slice()))
    );
  }

  closedEmitter(control: AbstractControl, controlName: string): void {
    const valuePossibleCity = control.value;
    let cityMaybe: ICountryCity[] | ICity[];
    if (controlName === 'workCity') {
      cityMaybe =
        typeof valuePossibleCity === 'string' && valuePossibleCity.length >= 3
          ? this._filterWorkCity(valuePossibleCity)
          : null;
    } else {
      cityMaybe =
        typeof valuePossibleCity === 'string' && valuePossibleCity.length >= 3
          ? this._filterHomeCity(valuePossibleCity)
          : null;
    }
    if (cityMaybe?.length) {
      control.setValue(cityMaybe[0]);
    }
  }

  onSubmit(): void {
    if (this.contactInformationForm.valid) {
      this.updateCustomerData();
      this.router.navigate([routesData.FINANTIAL_INFORMATION.redirectPath]);
    }
  }

  private _filterWorkCity(name: string): ICountryCity[] {
    const filterValue = name
      .toLowerCase()
      .normalize(this.normalizeForm)
      .replace(RegExps.DIACRITICAL_MARKS, '');

    return countryCities.filter((city) =>
      city.municipality
        .toLowerCase()
        .normalize(this.normalizeForm)
        .replace(RegExps.DIACRITICAL_MARKS, '')
        .includes(filterValue)
    );
  }

  private _filterHomeCity(name: string): ICity[] {
    const filterValue = name
      .toLowerCase()
      .normalize(this.normalizeForm)
      .replace(RegExps.DIACRITICAL_MARKS, '');

    return cities.filter((city) =>
      city.name
        .toLowerCase()
        .normalize(this.normalizeForm)
        .replace(RegExps.DIACRITICAL_MARKS, '')
        .includes(filterValue)
    );
  }

  private _filterWorkCityByCodeDane(code: number) {
    if (isNaN(code)) {
      return null;
    }

    const filter = countryCities.filter(
      (option) => (+option.codeDANE).toString() === code.toString()
    );

    return filter?.length ? filter[0] : null;
  }

  private _filterHomeCityByCodeDane(code: number) {
    if (isNaN(code)) {
      return null;
    }

    const filter = cities.filter(
      (city) => (+city.codeDANE).toString() === code.toString()
    );

    return filter?.length ? filter[0] : null;
  }
}
